import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { PageContainer, PageTitleBadge } from "../components";
import { FaChartBar } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import {
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "../styles/common.css";
import "./Rapports.css";

const Rapports = () => {
  const { t } = useTranslation();
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRapport = async () => {
      try {
        const response = await api.get("/api/rapport-cheptel/");
        setRapport(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement du rapport global:", err);
        setError(t.errorLoadingReport);
        setLoading(false);
      }
    };
    fetchRapport();
  }, []);

  const dataSexe = rapport?.repartition_sexe.map((item) => ({
    name: item.sexe === "M" ? t.males : t.females,
    value: item.total,
  }));

  const COLORS = ["#0088FE", "#FF8042"];

  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">{t.loadingReport}</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title={t.error} icon={<FaChartBar />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge title={t.herdReport} icon={<FaChartBar />} />

      {rapport && (
        <>
          {/* Section 1: Indicateurs Clés */}
          <div className="rapport-section">
            <h2 className="section-title">{t.keyIndicators}</h2>
            <div className="table-container-modern">
              <table className="rapport-table">
                <thead>
                  <tr>
                    <th>{t.element}</th>
                    <th style={{ textAlign: "center" }}>{t.value}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{t.initialStock}</td>
                    <td style={{ textAlign: "center" }}>{rapport.effectif_initial}</td>
                  </tr>
                  <tr>
                    <td>{t.births}</td>
                    <td style={{ color: "#10b981", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.naissances}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.mortality}</td>
                    <td style={{ color: "#ef4444", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.deces}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.purchases}</td>
                    <td style={{ color: "#3b82f6", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.achats}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.sales}</td>
                    <td style={{ color: "#f59e0b", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.ventes}
                    </td>
                  </tr>
                  <tr>
                    <td>{t.zakat}</td>
                    <td style={{ color: "#8b5cf6", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.zakat}
                    </td>
                  </tr>
                  <tr className="total-row">
                    <td><strong>{t.totalStock}</strong></td>
                    <td style={{ color: "#0f172a", fontWeight: "bold", textAlign: "center" }}>
                      {rapport.effectif_fin}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Statistiques Naissances */}
          {rapport["Statistiques Naissances"] && (
            <div className="rapport-section">
              <h2 className="section-title">{t.birthStatistics}</h2>
              <div className="table-container-modern">
                <table className="rapport-table">
                  <thead>
                    <tr>
                      <th>{t.indicator}</th>
                      <th style={{ textAlign: "center" }}>{t.value}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t.numberOfCalves}</td>
                      <td style={{ color: "#3b82f6", fontWeight: "bold", textAlign: "center" }}>
                        {rapport["Statistiques Naissances"].Veaux}
                      </td>
                    </tr>
                    <tr>
                      <td>{t.numberOfHeifers}</td>
                      <td style={{ color: "#10b981", fontWeight: "bold", textAlign: "center" }}>
                        {rapport["Statistiques Naissances"].Velles}
                      </td>
                    </tr>
                    <tr>
                      <td>{t.numberOfCows}</td>
                      <td style={{ textAlign: "center" }}>
                        {rapport["Statistiques Naissances"].Vaches}
                      </td>
                    </tr>
                    <tr>
                      <td>{t.birthRatePerCows}</td>
                      <td style={{ fontWeight: "bold", textAlign: "center" }}>
                        {Math.round(rapport["Statistiques Naissances"]["Taux de naissance par rapport aux vaches (%)"])}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Statistiques mortalité par catégorie */}
          {rapport["Statistiques mortalité par catégorie"] && (
            <div className="rapport-section">
              <h2 className="section-title">{t.mortalityStatistics}</h2>
              <div className="table-container-modern">
                <table className="rapport-table">
                  <thead>
                    <tr>
                      <th>{t.category}</th>
                      <th style={{ textAlign: "center" }}>{t.total}</th>
                      <th style={{ textAlign: "center" }}>{t.deaths}</th>
                      <th style={{ textAlign: "center" }}>{t.mortalityRate}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(rapport["Statistiques mortalité par catégorie"]).map(([categorie, stats]) => (
                      <tr key={categorie}>
                        <td>{categorie}</td>
                        <td style={{ textAlign: "center" }}>{stats.Total}</td>
                        <td style={{ fontWeight: "bold", textAlign: "center" }}>
                          {stats.Morts}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {Math.round(stats["Taux de mortalité (%)"])}
                        </td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>{t.global}</strong></td>
                      <td style={{ textAlign: "center" }}>
                        {Object.values(rapport["Statistiques mortalité par catégorie"]).reduce((sum, c) => sum + c.Total, 0)}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        {Object.values(rapport["Statistiques mortalité par catégorie"]).reduce((sum, c) => sum + c.Morts, 0)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {Math.round(rapport.taux_mortalite)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Répartition du Cheptel */}
          <div className="rapport-section">
            <h2 className="section-title">{t.herdDistribution}</h2>
            <div className="table-container-modern">
              <table className="rapport-table">
                <thead>
                  <tr>
                    <th>{t.category}</th>
                    <th style={{ textAlign: "center" }}>{t.stockCount}</th>
                  </tr>
                </thead>
                <tbody>
                  {rapport.repartition_categorie.map((item, index) => (
                    <tr key={index}>
                      <td>{capitalize(item.categorie)}</td>
                      <td style={{ textAlign: "center" }}>{item.total}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td><strong>{t.total}</strong></td>
                    <td style={{ textAlign: "center" }}>
                      <strong>{rapport.effectif_fin}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Répartition par Sexe */}
          <div className="rapport-section">
            <h2 className="section-title">{t.sexDistribution}</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dataSexe}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label
                  >
                    {dataSexe.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Valeur du Cheptel */}
          <div className="rapport-section valeur-section">
            <h2 className="section-title">{t.herdValue}</h2>
            <div className="valeur-container">
              <span className="valeur-amount">{rapport.valeur_totale_cheptel} FCFA</span>
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default Rapports;
